import type { ApiErrorBody, ApiSuccess, FieldError, ListMeta, RetryInfo } from "@closer/shared";

export class ApiRequestError extends Error {
  constructor(
    public code: string,
    message: string,
    public fields?: FieldError[],
    public status?: number,
    /**
     * Yerelleştirme anahtarı (CLOS-306). Bugün ekranlarda `message` gösteriliyor;
     * çok dil devreye girdiğinde metin bu anahtardan üretilecek ve `message` yalnız
     * yedek kalacak. Anahtarı şimdiden taşımak, o gün her çağrı yerini gezmemek demek.
     */
    public messageKey?: string,
    /** Bu istek güvenle tekrarlanabilir mi, ne kadar sonra. */
    public retry?: RetryInfo,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

/**
 * Tek HTTP giriş noktası (S10). Zarfı çözer; ok:false ise ApiRequestError fırlatır.
 * Kural: elle fetch/axios YASAK — her çağrı buradan geçer.
 */
/**
 * ÇEVRİMDIŞI KAPISI (CLOS-1278 · Readiness §5.9).
 *
 * ⚠ YALNIZ YAZMA kapatılıyor. Okuma açık kalıyor çünkü çevrimdışıyken de önbellekten
 * beslenen ekranlar çalışmaya devam etmeli; belgenin istediği "ekranı karart" değil,
 * "değişiklik yapma ve bunu söyle".
 *
 * ⚠ Kapı BURADA, butonlarda değil: tek HTTP giriş noktası burası (elle `fetch` yasak).
 * Yirmi ayrı yüzeye tek tek koysaydık biri unutulurdu ve unutulanın en kritik olanı
 * olmayacağının garantisi yok.
 *
 * ⚠ `navigator.onLine === false` GÜVENİLİR yön: tarayıcı "bağlantı yok" diyorsa gerçekten
 * yoktur. Tersi güvenilmez (online der ama ağ kopuk olabilir) — bu yüzden kapı yalnız
 * kesin bilgiyle kapanıyor, "online" iddiasına dayanarak hiçbir şey varsayılmıyor.
 */
function cevrimdisiMi(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

/** GET ve HEAD dışındaki her yöntem sunucuda durum değiştirebilir. */
function yazmaMi(method: string | undefined): boolean {
  const m = (method ?? "GET").toUpperCase();
  return m !== "GET" && m !== "HEAD";
}

export async function api<T>(
  path: string,
  init?: RequestInit & { tenantId?: string },
): Promise<{ data: T; meta?: ListMeta }> {
  const { tenantId, headers, ...rest } = init ?? {};

  if (yazmaMi(rest.method) && cevrimdisiMi()) {
    /**
     * ⚠ `retryable: true` DOĞRU ve önemli: istek ağa hiç çıkmadı, dolayısıyla sunucuda
     * yarım kalmış bir işlem yok. Bağlantı dönünce aynı işlem güvenle tekrarlanabilir.
     * `false` deseydik kullanıcıyı, aslında hiç olmamış bir işlem için tedirgin ederdik.
     */
    throw new ApiRequestError(
      "OFFLINE",
      "İnternet bağlantınız yok. Bağlantı döndüğünde tekrar deneyin.",
      undefined,
      undefined,
      "hata.cevrimdisi",
      { retryable: true },
    );
  }

  const response = await fetch(path, {
    credentials: "include",
    ...rest,
    headers: {
      ...(rest.body !== undefined && rest.body !== null
        ? { "Content-Type": "application/json" }
        : {}),
      ...(tenantId ? { "x-tenant-id": tenantId } : {}),
      ...(headers as Record<string, string> | undefined),
    },
  });

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new ApiRequestError(
      "INTERNAL",
      "Sunucudan beklenmeyen bir yanıt alındı. Lütfen tekrar deneyin.",
      undefined,
      response.status,
    );
  }

  const envelope = payload as ApiSuccess<T> | ApiErrorBody;
  if (!envelope.ok) {
    throw new ApiRequestError(
      envelope.error.code,
      envelope.error.message,
      envelope.error.fields,
      response.status,
      envelope.error.messageKey,
      envelope.error.retry,
    );
  }
  return { data: envelope.data, meta: envelope.meta };
}
